import { reminders, roadmapItems } from './database'
import { notificationService } from './notificationService'
import { currentUser } from './database'
import { integrationsEnabled, integrationsRequest } from '../lib/integrations'
import { reminderIsDue } from '../lib/reminderTiming'
import { localDateString } from '../lib/calendarDates'

// Reminder Engine - checks for reminders that should be sent and triggers notifications
export class ReminderEngine {
  constructor() {
    this.isRunning = false
    this.checkInterval = 60000 // Check every minute
  }

  // Start the reminder check loop
  start() {
    if (this.isRunning) {
      console.warn('ReminderEngine is already running')
      return
    }

    this.isRunning = true
    console.log('🚀 ReminderEngine started')
    this.checkReminders()
    this.intervalId = setInterval(() => this.checkReminders(), this.checkInterval)
  }

  // Stop the reminder check loop
  stop() {
    if (!this.isRunning) {
      console.warn('ReminderEngine is not running')
      return
    }

    this.isRunning = false
    clearInterval(this.intervalId)
    console.log('⛔ ReminderEngine stopped')
  }

  // Check for reminders that need to be sent
  async checkReminders() {
    if (this.checking) return
    this.checking = true
    try {
      const user = await currentUser()
      if (integrationsEnabled) {
        const result = await integrationsRequest('/reminders/check', { body: { time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone } })
        if (this.isRunning && (await currentUser()).id === user.id) {
          for (const notice of result.notices || []) window.dispatchEvent(new CustomEvent('spcc:reminder', { detail: notice }))
          window.dispatchEvent(new Event('spcc:reminders-updated'))
        }
        return
      }
      // Get all active reminders for the user
      const activeReminders = await reminders.getActiveReminders()
      console.log(`📋 Checking ${activeReminders.length} active reminders`)

      for (const reminder of activeReminders) {
        if (!this.isRunning) break
        if (reminder.user_id === user.id) await this.processReminder(reminder)
      }
      if (this.isRunning && (await currentUser()).id === user.id) window.dispatchEvent(new Event('spcc:reminders-updated'))
    } catch (error) {
      console.error('Error in checkReminders:', error)
    } finally {
      this.checking = false
    }
  }

  async processReminder(reminder) {
    try {
      // Skip if snoozed until a future date
      if (reminder.snoozed_until) {
        const snoozeDate = new Date(reminder.snoozed_until)
        if (snoozeDate > new Date()) {
          return
        }
        // Unsnoze if snooze period has passed
        await reminders.update(reminder.id, { status: 'active', snoozed_until: null })
      }

      // Check if reminder should be sent
      const shouldSend = this.shouldSendReminder(reminder)
      if (!shouldSend) {
        return
      }

      // Get the associated roadmap item
      const roadmapItem = await roadmapItems.getById(reminder.roadmap_item_id)
      if (!roadmapItem) {
        console.warn(`Roadmap item not found for reminder ${reminder.id}`)
        return
      }
      if (['done', 'completed', 'cancelled', 'archived'].includes(roadmapItem.status)) {
        await reminders.update(reminder.id, { status: 'completed', snoozed_until: null })
        return
      }

      // Send notifications via configured channels
      const delivered = reminder.delivered_channels || []
      const requested = reminder.notification_channels || ['in_app']
      const notificationChannels = requested.filter(channel => !delivered.includes(channel))
      const results = await notificationService.sendReminder(
        reminder,
        roadmapItem,
        notificationChannels
      )

      console.log(`✅ Sent reminder ${reminder.id}:`, results)

      // Mark reminder as sent
      const successful = [...new Set([...delivered, ...results.success])]
      await reminders.update(reminder.id, { delivered_channels: successful, is_sent: requested.every(channel => successful.includes(channel)) })
    } catch (error) {
      console.error(`Error processing reminder ${reminder.id}:`, error)
    }
  }

  // Determine if a reminder should be sent
  shouldSendReminder(reminder) {
    return reminderIsDue(reminder, Date.now(), Intl.DateTimeFormat().resolvedOptions().timeZone)
  }

  // Get reminders that are due today or overdue
  async getDueReminders() {
    return reminders.getDueReminders()
  }

  // Get reminders for the next N days
  async getUpcomingReminders(daysAhead = 30) {
    return reminders.getUpcomingReminders(daysAhead)
  }

  // Create a reminder for a roadmap item
  async createReminderForRoadmapItem(roadmapItemId, reminderConfig = {}) {
    try {
      const roadmapItem = await roadmapItems.getById(roadmapItemId)
      if (!roadmapItem) {
        throw new Error(`Roadmap item ${roadmapItemId} not found`)
      }

      const {
        reminderType = 'discovery_due',
        reminderDaysBefore = 7,
        notificationChannels = ['in_app'],
        dueAt = null,
        title = null,
        description = null
      } = reminderConfig

      if (!notificationChannels.length || notificationChannels.some(channel => !['in_app', 'push', 'slack'].includes(channel))) throw new Error('Choose at least one supported notification channel.')
      if (dueAt ? !Number.isFinite(Date.parse(dueAt)) || Date.parse(dueAt) <= Date.now() : !roadmapItem.target_date) throw new Error('Choose a future reminder time, or set the initiative target date.')

      const reminder = await reminders.create({
        roadmap_item_id: roadmapItemId,
        title: title || `Discovery due for: ${roadmapItem.title}`,
        description: description || `Complete discovery activities before the target date`,
        reminder_type: reminderType,
        target_date: roadmapItem.target_date || localDateString(new Date(dueAt)),
        ...(dueAt ? { due_at: new Date(dueAt).toISOString() } : {}),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        reminder_days_before: reminderDaysBefore,
        notification_channels: notificationChannels,
        is_sent: false,
        status: 'active'
      })

      console.log(`✅ Created reminder for roadmap item ${roadmapItemId}:`, reminder)
      return reminder
    } catch (error) {
      console.error('Error creating reminder:', error)
      throw error
    }
  }

  // Update reminder configuration
  async updateReminder(reminderId, updates) {
    try {
      const updated = await reminders.update(reminderId, updates)
      console.log(`✅ Updated reminder ${reminderId}:`, updated)
      return updated
    } catch (error) {
      console.error('Error updating reminder:', error)
      throw error
    }
  }

  // Snooze a reminder
  async snoozeReminder(reminderId, snoozeMinutes = 60) {
    try {
      const snoozeUntil = new Date(Date.now() + snoozeMinutes * 60 * 1000).toISOString()
      const updated = await reminders.snoozeReminder(reminderId, snoozeUntil)
      console.log(`✅ Snoozed reminder ${reminderId} until ${snoozeUntil}`)
      return updated
    } catch (error) {
      console.error('Error snoozing reminder:', error)
      throw error
    }
  }

  // Complete a reminder
  async completeReminder(reminderId) {
    try {
      const updated = await reminders.completeReminder(reminderId)
      console.log(`✅ Completed reminder ${reminderId}`)
      return updated
    } catch (error) {
      console.error('Error completing reminder:', error)
      throw error
    }
  }

  // Delete a reminder
  async deleteReminder(reminderId) {
    try {
      await reminders.delete(reminderId)
      console.log(`✅ Deleted reminder ${reminderId}`)
      return true
    } catch (error) {
      console.error('Error deleting reminder:', error)
      throw error
    }
  }

  // Get reminders for a specific roadmap item
  async getRemindersForRoadmapItem(roadmapItemId) {
    try {
      return await reminders.getRemindersForRoadmapItem(roadmapItemId)
    } catch (error) {
      console.error('Error fetching reminders for roadmap item:', error)
      return []
    }
  }

  // Auto-create reminders for roadmap items that don't have them
  async autoCreateReminders(roadmapItems, reminderDaysBefore = 7) {
    try {
      await currentUser()

      let created = 0
      for (const item of roadmapItems) {
        const existingReminders = await reminders.getRemindersForRoadmapItem(item.id)
        if (existingReminders.length === 0 && item.target_date) {
          await this.createReminderForRoadmapItem(item.id, {
            reminderDaysBefore,
            notificationChannels: ['in_app']
          })
          created++
        }
      }

      console.log(`✅ Auto-created ${created} reminders`)
      return created
    } catch (error) {
      console.error('Error auto-creating reminders:', error)
      throw error
    }
  }
}

export const reminderEngine = new ReminderEngine()
